import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import '../Styles/Terminal.scss';
import { portfolioContent, navigationUrls } from '../Content/portfolioContent';

const Terminal = ({ onFinish }) => {
  const [currentLine, setCurrentLine] = useState(0);
  const [showCursor, setShowCursor] = useState(true);
  const [loginPhase, setLoginPhase] = useState('boot'); // 'boot', 'typing', 'logged-in', 'interactive'
  const [typedText, setTypedText] = useState('');
  const [currentChar, setCurrentChar] = useState(0);
  const [inputText, setInputText] = useState('');
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [contactScript, setContactScript] = useState(null);
  const terminalRef = useRef(null);
  const inputRef = useRef(null);

  const username = 'Paul Walker';

  // Available files/sections using centralized content
  const files = useMemo(
    () => ({
      'about.txt': {
        content: portfolioContent.about.terminalFormat,
        action: 'about',
      },
      'projects.txt': {
        content: portfolioContent.projects.terminalFormat,
        action: 'projects',
      },
      'resume.txt': {
        content: portfolioContent.resume.terminalFormat,
        action: 'resume',
      },
      'contact.txt': {
        content: portfolioContent.contact.terminalFormat,
        action: 'contact',
      },
    }),
    []
  );

  const sections = useMemo(
    () => ['about', 'projects', 'resume', 'contact'],
    []
  );

  // Interactive terminal functions
  const addOutput = useCallback((text, type = 'default') => {
    setCommandHistory((prev) => [
      ...prev,
      { type, text, timestamp: Date.now() },
    ]);
  }, []);

  const listSections = useCallback(() => {
    addOutput('Portfolio sections:', 'command');
    sections.forEach((section) => {
      addOutput(`  ${section}/`, 'ls');
    });
    addOutput('', 'default');
  }, [addOutput, sections]);

  const navigateToSection = useCallback(
    (section) => {
      if (!section) {
        addOutput('cd: missing directory operand', 'error');
        return;
      }

      if (sections.includes(section)) {
        addOutput(`Navigating to ${section} section...`, 'command');
        // Navigate to the portfolio section
        setTimeout(() => {
          onFinish(); // Exit terminal to show main portfolio
          // Scroll to the section after a brief delay
          setTimeout(() => {
            const element = document.querySelector(navigationUrls[section]);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth' });
            }
          }, 100);
        }, 1000);
      } else {
        addOutput(`cd: ${section}: No such file or directory`, 'error');
      }
    },
    [addOutput, onFinish, sections]
  );

  const catFile = useCallback(
    (filename) => {
      if (!filename) {
        addOutput('cat: missing file operand', 'error');
        addOutput("Try 'cat --help' for more information.", 'error');
        return;
      }

      const file = files[filename];
      if (file) {
        // Display content with typing animation
        const lines = file.content.split('\n');
        let currentLineIndex = 0;

        const typeNextLine = () => {
          if (currentLineIndex < lines.length) {
            addOutput(lines[currentLineIndex], 'file');
            currentLineIndex++;
            setTimeout(typeNextLine, 50); // Fast typing between lines
          } else {
            addOutput('', 'default');
            // cat command only displays content, no navigation
          }
        };

        typeNextLine();
      } else {
        addOutput(`cat: ${filename}: No such file or directory`, 'error');
      }
    },
    [addOutput, files]
  );

  const clearTerminal = useCallback(() => {
    setCommandHistory([]);
  }, []);

  const showUser = useCallback(() => {
    addOutput(username, 'command');
  }, [addOutput]);

  const showDate = useCallback(() => {
    addOutput(new Date().toString(), 'command');
  }, [addOutput]);

  const runContactScript = useCallback(() => {
    setContactScript({
      step: 'name',
      data: {},
    });
    addOutput('Running contact script...', 'command');
    addOutput('', 'default');
  }, [addOutput]);

  const handleContactInput = useCallback(
    (input) => {
      if (!contactScript) return;

      const { step, data } = contactScript;

      if (step === 'name') {
        setContactScript({
          step: 'email',
          data: { ...data, name: input },
        });
        addOutput(`Name: ${input}`, 'command');
      } else if (step === 'email') {
        setContactScript({
          step: 'message',
          data: { ...data, email: input },
        });
        addOutput(`Email: ${input}`, 'command');
      } else if (step === 'message') {
        const contactData = { ...data, message: input };
        addOutput(`Message: ${input}`, 'command');
        addOutput('', 'default');
        addOutput('Sending contact form...', 'command');

        // Submit to Cloudflare Worker
        const formData = new FormData();
        formData.append('name', contactData.name);
        formData.append('email', contactData.email);
        formData.append('message', contactData.message);
        formData.append('website', ''); // Honeypot field

        fetch('https://portfolio.kasu.workers.dev/api/contact', {
          method: 'POST',
          body: formData,
        })
          .then(() => {
            addOutput('✅ Contact form submitted successfully!', 'success');
            addOutput(
              "Thank you for reaching out. I'll get back to you soon!",
              'info'
            );
          })
          .catch(() => {
            addOutput(
              '❌ Failed to submit contact form. Please try again.',
              'error'
            );
          });

        setContactScript(null);
      }
    },
    [contactScript, addOutput]
  );

  // Available interactive commands
  const interactiveCommands = useMemo(
    () => ({
      help: {
        description: 'Show available commands',
        usage: 'help',
        action: () => {
          addOutput('Available commands:', 'command');
          addOutput('  help       - Show available commands', 'help');
          addOutput('  ls         - List portfolio sections', 'help');
          addOutput('  cat        - Display file contents', 'help');
          addOutput('  cd         - Navigate to section', 'help');
          addOutput('  clear      - Clear terminal', 'help');
          addOutput('  whoami     - Show current user', 'help');
          addOutput('  date       - Show current date/time', 'help');
          addOutput('  ./contact.sh - Run contact script', 'help');
          addOutput('  contact    - Run contact script (alias)', 'help');
          addOutput('  exit       - Exit terminal', 'help');
          addOutput('', 'default');
        },
      },
      ls: {
        description: 'List portfolio sections',
        usage: 'ls [options]',
        action: () => listSections(),
      },
      cat: {
        description: 'Display file contents',
        usage: 'cat <filename>',
        action: (args) => catFile(args[0]),
      },
      cd: {
        description: 'Navigate to section',
        usage: 'cd <section>',
        action: (args) => navigateToSection(args[0]),
      },
      clear: {
        description: 'Clear terminal',
        usage: 'clear',
        action: () => clearTerminal(),
      },
      whoami: {
        description: 'Show current user',
        usage: 'whoami',
        action: () => showUser(),
      },
      date: {
        description: 'Show current date/time',
        usage: 'date',
        action: () => showDate(),
      },
      exit: {
        description: 'Exit terminal',
        usage: 'exit',
        action: () => onFinish(),
      },
      './contact.sh': {
        description: 'Run contact script',
        usage: './contact.sh',
        action: () => runContactScript(),
      },
      contact: {
        description: 'Run contact script (alias)',
        usage: 'contact',
        action: () => runContactScript(),
      },
    }),
    [
      listSections,
      catFile,
      navigateToSection,
      clearTerminal,
      showUser,
      showDate,
      runContactScript,
      onFinish,
      addOutput,
    ]
  );

  const executeCommand = useCallback(
    (input) => {
      const trimmed = input.trim();
      if (!trimmed) return;

      // Handle contact script input
      if (contactScript) {
        handleContactInput(trimmed);
        return;
      }

      addOutput(`$ ${trimmed}`, 'prompt');

      const [command, ...args] = trimmed.split(' ');
      const cmd = interactiveCommands[command];

      if (cmd) {
        cmd.action(args);
      } else {
        addOutput(`${command}: command not found`, 'error');
        addOutput("Type 'help' for available commands.", 'error');
      }
    },
    [interactiveCommands, addOutput, contactScript, handleContactInput]
  );

  // Simulate typing a command
  const simulateTyping = useCallback(
    (command) => {
      if (loginPhase !== 'interactive') return;

      let currentIndex = 0;
      const typeInterval = setInterval(() => {
        if (currentIndex < command.length) {
          setInputText(command.substring(0, currentIndex + 1));
          currentIndex++;
        } else {
          clearInterval(typeInterval);
          // Execute the command after typing is complete
          setTimeout(() => {
            executeCommand(command);
            setInputText('');
          }, 300);
        }
      }, 100); // Type each character every 100ms
    },
    [loginPhase, executeCommand]
  );

  // Ultra-fast Linux boot sequence commands with realistic timing
  const commands = useMemo(
    () => [
      {
        text: '[    0.000000] Linux version 5.15.0-portfolio (dev@portfolio-build) (gcc version 11.2.0)',
        delay: 2500, // Dramatic pause after first line
        type: 'kernel',
      },
      {
        text: '[    2.500000] Command line: BOOT_IMAGE=/vmlinuz root=/dev/sda1 ro quiet splash',
        delay: 15,
      },
      {
        text: '[    2.515000] PID hash table entries: 4096 (order: 3, 32768 bytes, linear)',
        delay: 15,
      },
      {
        text: '[    2.530000] Memory: 16384000K/16777216K available (16384K kernel)',
        delay: 15,
      },
      {
        text: '[    2.545000] SLUB: HWalign=64, Order=0-3, MinObjects=0, CPUs=8, Nodes=1',
        delay: 15,
      },
      {
        text: '[    2.560000] Kernel/User page tables isolation: enabled',
        delay: 15,
      },
      {
        text: '[    2.575000] rcu: Hierarchical RCU implementation.',
        delay: 15,
      },
      {
        text: '[    2.590000] NR_IRQS: 524544, nr_irqs: 2048, priv_irqs: 2048',
        delay: 15,
      },
      { text: '[    2.605000] Console: colour VGA+ 80x25', delay: 15 },
      { text: '[    2.620000] printk: console [tty0] enabled', delay: 15 },
      { text: '[    2.635000] ACPI: Core revision 20210730', delay: 15 },
      {
        text: '[    2.650000] clocksource: hpet: mask: 0xffffffff max_cycles: 0xffffffff',
        delay: 15,
      },
      {
        text: '[    2.665000] APIC: Switch to symmetric I/O mode setup',
        delay: 15,
      },
      {
        text: '[    2.680000] PCI: Using configuration type 1 for base access',
        delay: 15,
      },
      {
        text: '[    2.695000] HugeTLB registered 1.00 GiB page size, pre-allocated 0 pages',
        delay: 15,
      },
      {
        text: '[    2.710000] HugeTLB registered 2.00 MiB page size, pre-allocated 0 pages',
        delay: 15,
      },
      { text: '[    2.725000] ACPI: Added _OSI(Module Device)', delay: 15 },
      { text: '[    2.740000] ACPI: Added _OSI(Processor Device)', delay: 15 },
      {
        text: '[    2.755000] ACPI: Added _OSI(3.0 _SCP Extensions)',
        delay: 15,
      },
      {
        text: '[    2.770000] ACPI: 9 ACPI AML tables successfully acquired and loaded',
        delay: 15,
      },
      { text: '[    2.785000] ACPI: Interpreter enabled', delay: 15 },
      { text: '[    2.800000] ACPI: (supports S0 S3 S4 S5)', delay: 15 },
      {
        text: '[    2.815000] ACPI: Using IOAPIC for interrupt routing',
        delay: 15,
      },
      {
        text: '[    2.830000] PCI: Using host bridge windows from ACPI',
        delay: 15,
      },
      {
        text: '[    2.845000] ACPI: Enabled 2 GPEs in block 00 to 1F',
        delay: 15,
      },
      { text: '[    2.860000] SCSI subsystem initialized', delay: 15 },
      {
        text: '[    2.875000] usbcore: registered new interface driver usbfs',
        delay: 15,
      },
      {
        text: '[    2.890000] PCI: Root bus 00: using default resources',
        delay: 15,
      },
      { text: '[    2.905000] NetLabel: Initializing', delay: 15 },
      { text: '[    2.920000] NetLabel: domain hash size = 128', delay: 15 },
      {
        text: '[    2.935000] NetLabel: protocols = UNLABELED CIPSOv4 CALIPSO',
        delay: 15,
      },
      {
        text: '[    2.950000] clocksource: jiffies: mask: 0xffffffff max_cycles: 0xffffffff',
        delay: 15,
      },
      {
        text: '[    2.965000] futex hash table entries: 2048 (order: 5, 131072 bytes, linear)',
        delay: 15,
      },
      {
        text: '[    2.980000] pinctrl core: initialized pinctrl subsystem',
        delay: 15,
      },
      {
        text: '[    2.995000] PM: RTC time: 15:42:33, date: 2024/12/19',
        delay: 15,
      },
      { text: '[    3.010000] NET: Registered protocol family 16', delay: 15 },
      {
        text: '[    3.025000] audit: initializing netlink subsys (disabled)',
        delay: 15,
      },
      { text: '[    3.040000] cpuidle: using governor ladder', delay: 15 },
      { text: '[    3.055000] cpuidle: using governor menu', delay: 15 },
      {
        text: "[    3.070000] ACPI FADT declares the system doesn't support PCIe ASPM, so disable it",
        delay: 15,
      },
      { text: '[    3.085000] PCI: Using ACPI for IRQ routing', delay: 15 },
      {
        text: '[    3.100000] hpet0: at MMIO 0xfed00000, IRQs 2, 8, 0, 0, 0, 0, 0, 0',
        delay: 15,
      },
      {
        text: '[    3.115000] hpet0: 8 comparators, 64-bit 14.318180 MHz counter',
        delay: 15,
      },
      {
        text: '[    3.130000] clocksource: Switched to clocksource tsc-early',
        delay: 15,
      },
      { text: '[    3.145000] VFS: Disk quotas dquot_6.6.0', delay: 15 },
      {
        text: '[    3.160000] VFS: Dquot-cache hash table entries: 512 (order 0, 4096 bytes)',
        delay: 15,
      },
      { text: '[    3.175000] pnp: PnP ACPI init', delay: 15 },
      {
        text: '[    3.190000] system 00:00: [mem 0xfed00000-0xfed003ff] has been reserved',
        delay: 15,
      },
      { text: '[    3.205000] pnp: PnP ACPI: found 8 devices', delay: 15 },
      { text: '[    3.220000] NET: Registered protocol family 2', delay: 15 },
      {
        text: '[    3.235000] tcp_listen_portaddr_hash hash table entries: 8192',
        delay: 15,
      },
      {
        text: '[    3.250000] TCP established hash table entries: 131072',
        delay: 15,
      },
      { text: '[    3.265000] TCP bind hash table entries: 65536', delay: 15 },
      {
        text: '[    3.280000] TCP: Hash tables configured (established 131072 bind 65536)',
        delay: 15,
      },
      {
        text: '[    3.295000] UDP hash table entries: 8192 (order: 6, 262144 bytes, linear)',
        delay: 15,
      },
      {
        text: '[    3.310000] UDP-Lite hash table entries: 8192 (order: 6, 262144 bytes, linear)',
        delay: 15,
      },
      { text: '[    3.325000] NET: Registered protocol family 1', delay: 15 },
      { text: '[    3.340000] NET: Registered protocol family 44', delay: 15 },
      {
        text: '[    3.355000] pci 0000:00:02.0: Video device with shadowed ROM at [mem 0x000c0000-0x000dffff]',
        delay: 15,
      },
      { text: '[    3.370000] PCI: CLS 64 bytes, default 64', delay: 3500 }, // More dramatic pause while unpacking initramfs
      {
        text: '[    6.870000] Trying to unpack rootfs image as initramfs...',
        delay: 15,
      },
      { text: '[    6.885000] Freeing initrd memory: 25600K', delay: 2500 }, // More dramatic pause while scanning filesystems
      {
        text: '[    9.385000] check: Scanning for Btrfs filesystems',
        delay: 15,
      },
      {
        text: '[    9.400000] Block layer SCSI generic (bsg) driver version 0.4 loaded',
        delay: 15,
      },
      { text: '[    9.415000] io scheduler mq-deadline registered', delay: 15 },
      { text: '[    9.430000] io scheduler kyber registered', delay: 15 },
      {
        text: '[    9.445000] ahci 0000:00:1f.2: AHCI 0001.0300 32 slots 6 ports 6 Gbps 0x3f impl SATA mode',
        delay: 15,
      },
      {
        text: '[    9.460000] ahci 0000:00:1f.2: flags: 64bit ncq sntf pm led clo pio slum part ems apst',
        delay: 2000, // More dramatic pause while detecting SATA drive
      },
      {
        text: '[   11.460000] ata1: SATA link up 6.0 Gbps (SStatus 133 SControl 300)',
        delay: 15,
      },
      {
        text: '[   11.475000] ata1.00: ATA-8: PORTFOLIO-SSD-1TB, 1.0, max UDMA/133',
        delay: 15,
      },
      {
        text: '[   11.490000] ata1.00: 1953525168 sectors, multi 16: LBA48 NCQ (depth 32)',
        delay: 15,
      },
      { text: '[   11.505000] ata1.00: configured for UDMA/133', delay: 15 },
      {
        text: '[   11.520000] sd 0:0:0:0: [sda] 1953525168 512-byte logical blocks: (1.00 TB/931 GiB)',
        delay: 15,
      },
      {
        text: '[   11.535000] sd 0:0:0:0: [sda] Write cache: enabled, read cache: enabled',
        delay: 15,
      },
      { text: '[   11.550000] sda: sda1 sda2 < sda5 >', delay: 15 },
      {
        text: '[   11.565000] sd 0:0:0:0: [sda] Attached SCSI disk',
        delay: 15,
      },
      {
        text: '[   11.580000] Freeing unused kernel image (initmem) memory: 1644K',
        delay: 15,
      },
      {
        text: '[   11.595000] Write protecting the kernel read-only data: 18432k',
        delay: 15,
      },
      { text: '[   11.610000] Run /sbin/init as init process', delay: 15 },
      { text: '', delay: 15 },
      {
        text: 'systemd[1]: systemd 249.11-0ubuntu3.12 running in system mode.',
        delay: 15,
        type: 'systemd',
      },
      { text: 'systemd[1]: Detected architecture x86-64.', delay: 15 },
      { text: 'systemd[1]: Set hostname to <portfolio-system>.', delay: 15 },
      { text: 'systemd[1]: Reached target Local File Systems.', delay: 15 },
      { text: 'systemd[1]: Reached target Network.', delay: 15 },
      { text: 'systemd[1]: Starting OpenSSH server daemon...', delay: 15 },
      {
        text: 'systemd[1]: Starting Docker Application Container Engine...',
        delay: 15,
      },
      { text: 'systemd[1]: Starting PostgreSQL database server...', delay: 15 },
      { text: 'systemd[1]: Starting Redis In-Memory Data Store...', delay: 15 },
      { text: 'systemd[1]: Starting Nginx HTTP Server...', delay: 15 },
      { text: 'systemd[1]: Started OpenSSH server daemon.', delay: 15 },
      {
        text: 'systemd[1]: Started Docker Application Container Engine.',
        delay: 15,
      },
      { text: 'systemd[1]: Started PostgreSQL database server.', delay: 15 },
      { text: 'systemd[1]: Started Redis In-Memory Data Store.', delay: 15 },
      { text: 'systemd[1]: Started Nginx HTTP Server.', delay: 15 },
      { text: 'systemd[1]: Reached target Multi-User System.', delay: 15 },
      { text: '', delay: 15 },
      {
        text: '🚀 Welcome to Portfolio Linux - Backend Developer Edition',
        delay: 15,
        type: 'success',
      },
      {
        text: '🔥 System optimized for maximum performance',
        delay: 8,
        type: 'success',
      },
      {
        text: '⚡ All systems operational - Ready for development',
        delay: 12,
        type: 'success',
      },
      { text: '', delay: 20 },
      {
        text: '',
        delay: 20, // Delay before starting login sequence
        triggerLogin: true, // Special flag to trigger login sequence
      },
    ],
    []
  );

  // Instant line-by-line display
  useEffect(() => {
    // Only run boot sequence if we're in boot phase
    if (loginPhase === 'boot' && currentLine < commands.length) {
      const currentCommand = commands[currentLine];

      // Display line instantly after delay
      const timeoutId = setTimeout(() => {
        setCurrentLine(currentLine + 1);

        // Check if this command triggers the login sequence
        if (currentCommand.triggerLogin) {
          setLoginPhase('typing');
        }
      }, currentCommand.delay);

      return () => clearTimeout(timeoutId);
    } else if (loginPhase === 'boot' && currentLine >= commands.length) {
      // Only finish if we're still in boot phase (not typing or logged in)
      const timeoutId = setTimeout(() => {
        onFinish();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [currentLine, commands, onFinish, loginPhase]);

  // Login typing effect
  useEffect(() => {
    if (loginPhase === 'typing') {
      // Initial delay before starting to type
      if (currentChar === 0 && typedText === '') {
        const timeoutId = setTimeout(() => {
          setCurrentChar(1);
          setTypedText(username.charAt(0));
        }, 1200); // 1.2 second delay before starting to type

        return () => clearTimeout(timeoutId);
      }

      // Continue typing
      if (currentChar > 0 && currentChar < username.length) {
        const timeoutId = setTimeout(() => {
          setCurrentChar(currentChar + 1);
          setTypedText(username.substring(0, currentChar + 1));
        }, 150); // Type each character with 150ms delay for better visibility

        return () => clearTimeout(timeoutId);
      }

      // Finished typing username, wait a second then "hit enter"
      if (currentChar === username.length) {
        const timeoutId = setTimeout(() => {
          setLoginPhase('logged-in');
          // Clear terminal and show interactive prompt
          setTimeout(() => {
            setLoginPhase('interactive');
            setCurrentLine(0); // Clear boot messages
            setCommandHistory([]); // Clear any existing history
            addOutput("Welcome to Paul Walker's Portfolio", 'success');
            addOutput("Type 'help' for available commands.", 'info');
            addOutput('', 'default');
          }, 500);
        }, 1000);

        return () => clearTimeout(timeoutId);
      }
    }
  }, [loginPhase, currentChar, typedText, username, onFinish, addOutput]);

  // Cursor blinking effect
  useEffect(() => {
    const interval = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 530);
    return () => clearInterval(interval);
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [currentLine, commandHistory]);

  // Interactive terminal keyboard handling
  useEffect(() => {
    if (loginPhase === 'interactive') {
      const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          executeCommand(inputText);
          setInputText('');
          setHistoryIndex(-1);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          if (
            commandHistory.length > 0 &&
            historyIndex < commandHistory.length - 1
          ) {
            const newIndex = historyIndex + 1;
            setHistoryIndex(newIndex);
            const command =
              commandHistory[commandHistory.length - 1 - newIndex];
            if (command && command.type === 'prompt') {
              setInputText(command.text.replace('$ ', ''));
            }
          }
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (historyIndex > 0) {
            const newIndex = historyIndex - 1;
            setHistoryIndex(newIndex);
            const command =
              commandHistory[commandHistory.length - 1 - newIndex];
            if (command && command.type === 'prompt') {
              setInputText(command.text.replace('$ ', ''));
            } else {
              setInputText('');
            }
          } else if (historyIndex === 0) {
            setHistoryIndex(-1);
            setInputText('');
          }
        } else if (e.key === 'Backspace') {
          e.preventDefault();
          setInputText((prev) => prev.slice(0, -1));
        } else if (e.key.length === 1) {
          e.preventDefault();
          setInputText((prev) => prev + e.key);
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      // Skip animation on any key press during boot/login
      const handleKeyPress = () => {
        setLoginPhase('interactive');
        setCurrentLine(0); // Clear boot messages
        setCommandHistory([]); // Clear any existing history
        addOutput("Welcome to Paul Walker's Portfolio", 'success');
        addOutput("Type 'help' for available commands.", 'info');
        addOutput('', 'default');
      };

      document.addEventListener('keydown', handleKeyPress);
      return () => {
        document.removeEventListener('keydown', handleKeyPress);
      };
    }
  }, [
    loginPhase,
    inputText,
    commandHistory,
    historyIndex,
    onFinish,
    executeCommand,
    addOutput,
  ]);

  const getLineClass = (command) => {
    if (command.type === 'kernel') return 'kernel-line';
    if (command.type === 'systemd') return 'systemd-line';
    if (command.type === 'success') return 'success-line';
    if (command.type === 'prompt') return 'prompt-line';
    return 'default-line';
  };

  return (
    <div className="terminal-window">
      <div className="terminal-header">
        <div className="terminal-left-controls">
          <div className="control-button new-terminal" title="New Terminal">
            <span className="plus-icon">+</span>
          </div>
        </div>
        <div className="terminal-title">
          Terminal — paul@portfolio-system: ~
        </div>
        <div className="terminal-right-controls">
          <div className="control-button search" title="Search">
            <img
              src="/magnifying-glass.svg"
              alt="Search"
              className="search-icon"
              style={{ width: '14px', height: '14px' }}
            />
          </div>
          <div className="control-button menu" title="Menu">
            <span className="menu-icon">☰</span>
          </div>
          <div className="control-button minimize" title="Minimize">
            <span className="minimize-icon">—</span>
          </div>
          <div className="control-button maximize" title="Maximize">
            <span className="maximize-icon">□</span>
          </div>
          <div className="control-button close" title="Close">
            <span className="close-icon">×</span>
          </div>
        </div>
      </div>
      <div className="terminal" ref={terminalRef}>
        {commands.slice(0, currentLine).map((command, index) => (
          <p key={index} className={getLineClass(command)}>
            {command.text}
          </p>
        ))}
        {currentLine < commands.length && loginPhase === 'boot' && (
          <p className={getLineClass(commands[currentLine])}>
            {commands[currentLine].text}
            {commands[currentLine].showCursor && showCursor && (
              <span className="cursor">_</span>
            )}
          </p>
        )}

        {/* Login typing sequence */}
        {loginPhase === 'typing' && (
          <p className="prompt-line">
            portfolio-system login: {typedText}
            {showCursor && <span className="cursor">_</span>}
          </p>
        )}

        {/* Logged in state */}
        {loginPhase === 'logged-in' && (
          <p className="prompt-line">
            portfolio-system login: {username}
            <br />
            Last login: {new Date().toLocaleString()}
            <br />
            Welcome to Portfolio Linux!
          </p>
        )}

        {/* Interactive terminal */}
        {loginPhase === 'interactive' && (
          <>
            {/* Command buttons - sticky at top */}
            <div className="command-buttons">
              <button
                className="cmd-btn"
                onClick={() => simulateTyping('help')}
                title="Show available commands"
              >
                help
              </button>
              <button
                className="cmd-btn"
                onClick={() => simulateTyping('ls')}
                title="List portfolio sections"
              >
                ls
              </button>
              <button
                className="cmd-btn"
                onClick={() => simulateTyping('cd about')}
                title="Navigate to about section"
              >
                cd about
              </button>
              <button
                className="cmd-btn"
                onClick={() => simulateTyping('cat about.txt')}
                title="View about information in terminal"
              >
                cat about.txt
              </button>
              <button
                className="cmd-btn"
                onClick={() => simulateTyping('cd projects')}
                title="Navigate to projects section"
              >
                cd projects
              </button>
              <button
                className="cmd-btn"
                onClick={() => simulateTyping('cd resume')}
                title="Navigate to resume section"
              >
                cd resume
              </button>
              <button
                className="cmd-btn"
                onClick={() => simulateTyping('cd contact')}
                title="Navigate to contact section"
              >
                cd contact
              </button>
              <button
                className="cmd-btn"
                onClick={() => simulateTyping('clear')}
                title="Clear terminal"
              >
                clear
              </button>
              <button
                className="cmd-btn"
                onClick={() => simulateTyping('./contact.sh')}
                title="Run contact script"
              >
                ./contact.sh
              </button>
            </div>

            {/* Terminal content area */}
            <div className="terminal-content">
              {commandHistory.map((item, index) => (
                <p
                  key={index}
                  className={`${getLineClass({ type: item.type })}`}
                >
                  {item.text}
                </p>
              ))}
              <div className="input-line">
                <span className="prompt">
                  {contactScript
                    ? contactScript.step === 'name'
                      ? 'Name: '
                      : contactScript.step === 'email'
                      ? 'Email: '
                      : 'Message: '
                    : 'paul@portfolio:~$ '}
                </span>
                <span className="input-text">{inputText}</span>
                {showCursor && <span className="cursor">_</span>}
              </div>
            </div>

            {/* Hidden input for focus */}
            <input
              ref={inputRef}
              type="text"
              className="hidden-input"
              autoFocus
              spellCheck="false"
              tabIndex={-1}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default Terminal;
